import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useAxios from '../useAxios/useAxios';
import useLocalisedMoment from '../useLocalisedMoment/useLocalisedMoment';

// this mechanism will cache any in-flight requests
// this prevents multiple requests for the same rate content that are happening in the same render cycle

const ongoingRequests = new Map();

const useRateContent = (
  ratePlanCode,
  productCode,
  { startDate, endDate, numAdults = 2, children = [], agentId }
) => {
  const moment = useLocalisedMoment();
  const axios = useAxios();
  const [rateContent, setRateContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentLanguage = useSelector(
    (state) => state.appSettings.languages.current
  );

  const highestAgentCommissionEnabled = useSelector(
    (state) => state.appSettings.featureFlags.highestAgentCommissionEnabled
  );

  const params = useMemo(
    () => ({
      ratePlanCode,
      startDate,
      endDate,
      numAdults,
      children,
      productCode,
      agentId,
      currentLanguage,
    }),
    [
      ratePlanCode,
      startDate,
      endDate,
      numAdults,
      children,
      productCode,
      agentId,
      currentLanguage,
    ]
  );

  const cacheKey = useMemo(
    () =>
      `${productCode}-${ratePlanCode}-${JSON.stringify({
        startDate,
        endDate,
        numAdults,
        children,
        agentId,
        currentLanguage,
      })}`,
    [
      productCode,
      ratePlanCode,
      startDate,
      endDate,
      numAdults,
      children,
      agentId,
      currentLanguage,
    ]
  );

  const shouldFetchContent = useCallback(() => {
    if (!startDate || !ratePlanCode || !productCode || !endDate) {
      return false;
    }

    const start = moment(startDate);
    const end = moment(endDate);

    return (
      start.isValid() &&
      end.isValid() &&
      start.isAfter(moment().subtract(1, 'day'))
    );
  }, [startDate, endDate, ratePlanCode, productCode, moment]);

  const agentCommission = useGetAgentCommission(
    highestAgentCommissionEnabled,
    rateContent,
    agentId
  );

  useEffect(() => {
    let isMounted = true;

    const fetchContent = async () => {
      if (!shouldFetchContent()) {
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      if (isMounted) {
        setLoading(true);
        setError(null);
      }

      try {
        let dataPromise;

        if (ongoingRequests.has(cacheKey)) {
          dataPromise = ongoingRequests.get(cacheKey);
        } else {
          dataPromise = axios
            .get('/rate-content', { params })
            .then((response) => response.data);

          ongoingRequests.set(cacheKey, dataPromise);

          dataPromise.finally(() => {
            ongoingRequests.delete(cacheKey);
          });
        }

        const data = await dataPromise;

        if (isMounted && data) {
          setRateContent(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchContent();

    return () => {
      isMounted = false;
    };
  }, [cacheKey, params, axios, shouldFetchContent]);

  return { rateContent, loading, error, agentCommission };
};

const useGetAgentCommission = (
  highestAgentCommissionEnabled,
  rateContent,
  agentId
) => {
  return (
    useSelector((state) => {
      const rateAgentCommission = rateContent?.commissionablePercentage;
      if (highestAgentCommissionEnabled) {
        return state.belmond.agents[agentId]?.commission;
      }
      return rateAgentCommission;
    }) || 0
  );
};

export default useRateContent;
